import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import TerminalIcon from '@mui/icons-material/Terminal';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';

export interface SseLogEntry {
  id: string;
  timestamp: number;
  channel: string;
  action: string | null;
  device: string | null;
  asset: string | null;
  details: string | null;
  batteryLevel: number | null;
  name: string | null;
  raw: string;
  isJson: boolean;
}

interface SseLogsViewerProps {
  logs: SseLogEntry[];
  onClear: () => void;
  // symbotId -> bumperNumber, so the device filter shows the same identity
  // as the rest of the screen instead of the raw device id
  deviceLabels?: Map<string, string>;
}

// same reasoning as Haul Monitor's STATUS_COLORS — a fixed terminal look
// deliberately lives outside the theme token system, same as the screen
// this was lifted from
const TERMINAL = {
  bg: '#0d1117',
  chrome: '#161b22',
  border: 'rgba(255, 255, 255, 0.14)',
  text: '#e6edf3',
  textDim: '#7d8590',
  bright: '#79c0ff',
  warn: '#ffa657',
  error: '#f85149',
  hover: 'rgba(255, 255, 255, 0.06)',
  mono: 'ui-monospace, SFMono-Regular, "JetBrains Mono", "Fira Code", Menlo, Consolas, monospace',
};

const TerminalCursor: React.FC = () => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: 7,
      height: 13,
      ml: 0.5,
      bgcolor: TERMINAL.text,
      verticalAlign: 'text-bottom',
      animation: 'sse-logs-blink 1s steps(1, end) infinite',
      '@keyframes sse-logs-blink': {
        '0%, 50%': { opacity: 1 },
        '50.01%, 100%': { opacity: 0 },
      },
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    }}
  />
);

// Reusable raw-SSE log viewer (terminal-skin), lifted from Haul Monitor so
// any screen with a live dispatch SSE subscription can offer the same
// dev/internal-only log inspector. The caller owns the SSE connection and
// log buffering (mirrors what it already needs for its own live-update
// state) and just hands the resulting array in as `logs`.
export const SseLogsViewer: React.FC<SseLogsViewerProps> = ({
  logs,
  onClear,
  deviceLabels,
}) => {
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const formatLogTime = (timestamp: number) =>
    convertUtcToDisplayTime(
      timestamp,
      preferredTimeDisplayMode,
      timezone,
      'HH:mm:ss'
    );
  const [modalOpen, setModalOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [search, setSearch] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  // GPS pubsub messages fire far more often than anything else on this
  // channel and drown out the actions/status pings people actually come
  // here to read, so they're hidden by default
  const [hideGps, setHideGps] = useState(true);
  const [paused, setPaused] = useState(false);
  // frozen view of `logs` captured the instant Pause is toggled on — the
  // caller's SSE subscription keeps appending to the live `logs` prop
  // regardless, so pausing has to mean "stop reading from it", not "ask the
  // caller to stop sending"
  const [pausedSnapshot, setPausedSnapshot] = useState<SseLogEntry[] | null>(
    null
  );
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const togglePaused = () => {
    setPaused((prev) => {
      const next = !prev;
      setPausedSnapshot(next ? logs : null);
      return next;
    });
  };

  const displayedLogs = paused && pausedSnapshot ? pausedSnapshot : logs;

  const deviceOptions = useMemo(() => {
    const devices = new Set<string>();
    displayedLogs.forEach((log) => {
      if (log.device) devices.add(log.device);
    });
    return Array.from(devices)
      .map((device) => ({
        device,
        label: deviceLabels?.get(device) ?? device,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [displayedLogs, deviceLabels]);

  const channelOptions = useMemo(() => {
    const channels = new Set<string>();
    displayedLogs.forEach((log) => {
      if (log.channel) channels.add(log.channel);
    });
    return Array.from(channels).sort();
  }, [displayedLogs]);

  const filteredLogs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return displayedLogs.filter((log) => {
      if (hideGps && log.name?.toLowerCase() === 'gps') return false;
      if (deviceFilter !== 'all' && log.device !== deviceFilter) return false;
      if (channelFilter !== 'all' && log.channel !== channelFilter) {
        return false;
      }
      if (!term) return true;
      return (log.raw ?? '').toLowerCase().includes(term);
    });
  }, [displayedLogs, search, deviceFilter, channelFilter, hideGps]);

  const mono = TERMINAL.mono;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<TerminalIcon fontSize="small" />}
        onClick={() => {
          setModalOpen(true);
        }}
        sx={{ textTransform: 'none' }}
      >
        {t('logs_viewer_button')}
      </Button>

      {modalOpen &&
        minimized &&
        createPortal(
          <Box
            sx={{
              position: 'fixed',
              right: 16,
              bottom: 16,
              zIndex: 2000,
              width: 340,
              borderRadius: 1.5,
              overflow: 'hidden',
              bgcolor: TERMINAL.bg,
              border: `1px solid ${TERMINAL.border}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            }}
          >
            <Box
              onClick={() => {
                setMinimized(false);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                bgcolor: TERMINAL.chrome,
                borderBottom: `1px solid ${TERMINAL.border}`,
                '&:hover': { bgcolor: TERMINAL.hover },
              }}
            >
              <TerminalIcon fontSize="small" sx={{ color: TERMINAL.textDim }} />
              <Typography
                sx={{
                  fontFamily: mono,
                  fontSize: 12,
                  fontWeight: 700,
                  color: TERMINAL.text,
                }}
              >
                {t('logs_viewer_modal_title')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: mono,
                  fontSize: 11,
                  color: TERMINAL.textDim,
                  ml: 'auto',
                }}
              >
                {filteredLogs.length}
              </Typography>
            </Box>
            <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
              {filteredLogs.slice(0, 8).map((log) => (
                <Box
                  key={log.id}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderBottom: `1px solid ${TERMINAL.border}`,
                    '&:last-of-type': { borderBottom: 'none' },
                    display: 'flex',
                    gap: 0.75,
                    alignItems: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      fontFamily: mono,
                      fontSize: 10,
                      color: TERMINAL.textDim,
                      flexShrink: 0,
                    }}
                  >
                    {formatLogTime(log.timestamp)}
                  </Typography>
                  {log.action && (
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: mono,
                        fontSize: 10,
                        fontWeight: 700,
                        color: TERMINAL.text,
                        flexShrink: 0,
                      }}
                    >
                      {log.action}
                    </Typography>
                  )}
                  {log.asset && (
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: mono,
                        fontSize: 10,
                        color: TERMINAL.bright,
                      }}
                    >
                      {log.asset}
                    </Typography>
                  )}
                </Box>
              ))}
              {filteredLogs.length === 0 && (
                <Typography
                  sx={{
                    fontFamily: mono,
                    fontSize: 11,
                    color: TERMINAL.textDim,
                    p: 1.5,
                    textAlign: 'center',
                  }}
                >
                  {t('logs_viewer_empty')}
                </Typography>
              )}
            </Box>
          </Box>,
          document.body
        )}

      <Dialog
        open={modalOpen && !minimized}
        onClose={() => {
          setModalOpen(false);
          setMinimized(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundImage: 'none',
            bgcolor: TERMINAL.bg,
            border: `1px solid ${TERMINAL.border}`,
            boxShadow: `0 0 0 1px ${TERMINAL.border}, 0 20px 60px rgba(0,0,0,0.6)`,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.25,
            bgcolor: TERMINAL.chrome,
            borderBottom: `1px solid ${TERMINAL.border}`,
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Tooltip title={t('logs_viewer_close')}>
              <Box
                onClick={() => {
                  setModalOpen(false);
                  setMinimized(false);
                }}
                sx={{
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  bgcolor: '#ff5f57',
                  cursor: 'pointer',
                  '&:hover': { filter: 'brightness(1.15)' },
                }}
              />
            </Tooltip>
            <Tooltip title={t('logs_viewer_minimize')}>
              <Box
                onClick={() => {
                  setMinimized(true);
                }}
                sx={{
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  bgcolor: '#febc2e',
                  cursor: 'pointer',
                  '&:hover': { filter: 'brightness(1.15)' },
                }}
              />
            </Tooltip>
            <Box
              sx={{
                width: 11,
                height: 11,
                borderRadius: '50%',
                bgcolor: '#28c840',
              }}
            />
          </Box>
          <Typography
            sx={{
              ml: 1,
              fontFamily: mono,
              fontSize: 12,
              color: TERMINAL.textDim,
            }}
          >
            symx@logs:~$
          </Typography>
        </Box>

        <DialogContent sx={{ bgcolor: TERMINAL.bg, pt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
              mb: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder={t('logs_viewer_search_placeholder') ?? ''}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      sx={{
                        fontFamily: mono,
                        color: TERMINAL.textDim,
                        fontSize: 13,
                      }}
                    >
                      {'>'}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  fontFamily: mono,
                  color: TERMINAL.text,
                  fontSize: 13,
                  bgcolor: 'rgba(255,255,255,0.02)',
                  '& fieldset': { borderColor: TERMINAL.border },
                  '&:hover fieldset': { borderColor: TERMINAL.text },
                  '&.Mui-focused fieldset': { borderColor: TERMINAL.text },
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: TERMINAL.textDim,
                  opacity: 1,
                },
              }}
            />

            <Select
              size="small"
              value={deviceFilter}
              onChange={(e) => {
                setDeviceFilter(e.target.value);
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: TERMINAL.chrome,
                    border: `1px solid ${TERMINAL.border}`,
                    '& .MuiMenuItem-root': {
                      fontFamily: mono,
                      color: TERMINAL.text,
                      fontSize: 13,
                    },
                    '& .MuiMenuItem-root:hover': { bgcolor: TERMINAL.hover },
                  },
                },
              }}
              sx={{
                minWidth: 170,
                fontFamily: mono,
                color: TERMINAL.text,
                fontSize: 13,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: TERMINAL.border,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: TERMINAL.text,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: TERMINAL.text,
                },
                '.MuiSvgIcon-root': { color: TERMINAL.textDim },
              }}
            >
              <MenuItem value="all">
                {t('logs_viewer_asset_filter_all')}
              </MenuItem>
              {deviceOptions.map((opt) => (
                <MenuItem key={opt.device} value={opt.device}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>

            <Select
              size="small"
              value={channelFilter}
              onChange={(e) => {
                setChannelFilter(e.target.value);
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: TERMINAL.chrome,
                    border: `1px solid ${TERMINAL.border}`,
                    '& .MuiMenuItem-root': {
                      fontFamily: mono,
                      color: TERMINAL.text,
                      fontSize: 13,
                    },
                    '& .MuiMenuItem-root:hover': { bgcolor: TERMINAL.hover },
                  },
                },
              }}
              sx={{
                minWidth: 170,
                fontFamily: mono,
                color: TERMINAL.text,
                fontSize: 13,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: TERMINAL.border,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: TERMINAL.text,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: TERMINAL.text,
                },
                '.MuiSvgIcon-root': { color: TERMINAL.textDim },
              }}
            >
              <MenuItem value="all">
                {t('logs_viewer_channel_filter_all')}
              </MenuItem>
              {channelOptions.map((channel) => (
                <MenuItem key={channel} value={channel}>
                  {channel}
                </MenuItem>
              ))}
            </Select>

            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={hideGps}
                  onChange={(e) => {
                    setHideGps(e.target.checked);
                  }}
                  sx={{
                    color: TERMINAL.textDim,
                    p: 0.5,
                    '&.Mui-checked': { color: TERMINAL.bright },
                  }}
                />
              }
              label={t('logs_viewer_hide_gps')}
              sx={{
                m: 0,
                whiteSpace: 'nowrap',
                '& .MuiFormControlLabel-label': {
                  fontFamily: mono,
                  fontSize: 12,
                  color: TERMINAL.text,
                },
              }}
            />

            <Button
              size="small"
              sx={{
                textTransform: 'none',
                whiteSpace: 'nowrap',
                fontFamily: mono,
                fontSize: 12,
                color: TERMINAL.text,
                border: `1px solid ${TERMINAL.border}`,
                '&:hover': {
                  borderColor: TERMINAL.text,
                  bgcolor: TERMINAL.hover,
                },
              }}
              onClick={() => {
                togglePaused();
              }}
            >
              {paused
                ? `:${t('logs_viewer_resume')}`
                : `:${t('logs_viewer_pause')}`}
            </Button>

            <Button
              size="small"
              sx={{
                textTransform: 'none',
                whiteSpace: 'nowrap',
                fontFamily: mono,
                fontSize: 12,
                color: TERMINAL.text,
                border: `1px solid ${TERMINAL.border}`,
                '&:hover': {
                  borderColor: TERMINAL.text,
                  bgcolor: TERMINAL.hover,
                },
              }}
              onClick={onClear}
            >
              {`:${t('logs_viewer_clear')}`}
            </Button>
          </Box>

          {paused && (
            <Typography
              sx={{
                fontFamily: mono,
                fontSize: 12,
                color: TERMINAL.warn,
                mb: 1,
              }}
            >
              # {t('logs_viewer_paused_notice')}
            </Typography>
          )}

          <Typography
            sx={{
              fontFamily: mono,
              fontSize: 12,
              color: TERMINAL.textDim,
              mb: 1,
            }}
          >
            # {t('logs_viewer_count', { count: filteredLogs.length })}
          </Typography>

          <Box
            sx={{
              maxHeight: '55vh',
              overflowY: 'auto',
              bgcolor: '#060a08',
              border: `1px solid ${TERMINAL.border}`,
              borderRadius: 1,
              fontFamily: mono,
            }}
          >
            {filteredLogs.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: mono,
                    fontSize: 13,
                    color: TERMINAL.textDim,
                  }}
                >
                  $ {t('logs_viewer_empty')}
                </Typography>
                <TerminalCursor />
              </Box>
            ) : (
              filteredLogs.map((log) => {
                const isExpanded = expandedIds.has(log.id);
                const isBatteryLow =
                  log.batteryLevel != null && log.batteryLevel <= 20;
                return (
                  <Box
                    key={log.id}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderBottom: `1px solid ${TERMINAL.border}`,
                      '&:last-of-type': { borderBottom: 'none' },
                      '&:hover': { bgcolor: TERMINAL.hover },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        mb: 0.5,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: mono,
                          fontSize: 11,
                          color: TERMINAL.textDim,
                        }}
                      >
                        [{formatLogTime(log.timestamp)}]
                      </Typography>
                      {log.channel && (
                        <Typography
                          sx={{
                            fontFamily: mono,
                            fontSize: 11,
                            color: TERMINAL.textDim,
                          }}
                        >
                          [{log.channel}]
                        </Typography>
                      )}
                      {log.action && (
                        <Typography
                          sx={{
                            fontFamily: mono,
                            fontSize: 11,
                            fontWeight: 700,
                            color: TERMINAL.text,
                          }}
                        >
                          {log.action}
                        </Typography>
                      )}
                      {log.asset && (
                        <Typography
                          sx={{
                            fontFamily: mono,
                            fontSize: 11,
                            color: TERMINAL.bright,
                          }}
                        >
                          {log.asset}
                        </Typography>
                      )}
                      {isBatteryLow && (
                        <Typography
                          sx={{
                            fontFamily: mono,
                            fontSize: 11,
                            fontWeight: 700,
                            color: TERMINAL.warn,
                          }}
                        >
                          [WARN] {t('logs_viewer_battery_low')}
                        </Typography>
                      )}
                      {log.isJson && (
                        <IconButton
                          size="small"
                          sx={{ ml: 'auto', p: 0.25, color: TERMINAL.textDim }}
                          onClick={() => {
                            setExpandedIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(log.id)) {
                                next.delete(log.id);
                              } else {
                                next.add(log.id);
                                if (!paused) togglePaused();
                              }
                              return next;
                            });
                          }}
                        >
                          {isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      )}
                    </Box>
                    {log.details && (
                      <Typography
                        sx={{
                          fontFamily: mono,
                          fontSize: 12,
                          color: TERMINAL.text,
                          mb: 0.5,
                        }}
                      >
                        # {log.details}
                      </Typography>
                    )}
                    {(!log.isJson || isExpanded) && (
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1,
                          borderLeft: `2px solid ${TERMINAL.border}`,
                          fontSize: 11,
                          fontFamily: mono,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: TERMINAL.text,
                        }}
                      >
                        {log.raw}
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
            {filteredLogs.length > 0 && (
              <Box sx={{ px: 1.5, py: 1 }}>
                <Typography
                  component="span"
                  sx={{ fontFamily: mono, fontSize: 12, color: TERMINAL.text }}
                >
                  $
                </Typography>
                <TerminalCursor />
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, bgcolor: TERMINAL.bg }}>
          <Button
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontFamily: mono,
              fontSize: 12,
              color: TERMINAL.bg,
              bgcolor: TERMINAL.text,
              '&:hover': { bgcolor: TERMINAL.bright },
            }}
            onClick={() => {
              setModalOpen(false);
              setMinimized(false);
            }}
          >
            {t('logs_viewer_close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SseLogsViewer;
